import React from 'react';
import { Modal, Button, Table } from 'react-bootstrap';
import { HotIssue } from '../types';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { hotIssueApi } from '../services/api';

interface HotIssueModalProps {
  hotIssue: HotIssue | null;
  show: boolean;
  onHide: () => void;
}

const HotIssueModal: React.FC<HotIssueModalProps> = ({ hotIssue, show, onHide }) => {
  const [showToast, setShowToast] = React.useState(false);
  const [items, setItems] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const fetchItems = async () => {
      if (!hotIssue) return;
      setLoading(true);
      try {
        const data = await hotIssueApi.getHotIssueItems(hotIssue.id);
        setItems(data.slice(0, 10));
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    if (show && hotIssue) fetchItems();
  }, [hotIssue, show]);

  if (!hotIssue) return null;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="chart-modal"
      contentClassName="theme-card border-0 shadow-lg"
    >
      <Modal.Header closeButton className="gradient-bg-primary text-white border-0"
        style={{
          borderTopLeftRadius: '1.5rem',
          borderTopRightRadius: '1.5rem',
          padding: '2.2rem 2.2rem 1.2rem 2.2rem',
          minHeight: 90,
          background: 'linear-gradient(90deg, #ef4444 0%, #fde68a 100%)'
        }}
      >
        <div className="w-100 d-flex justify-content-between align-items-center flex-wrap">
          <h2 className="fw-bold mb-0" style={{ letterSpacing: '-0.01em', fontSize: '2rem' }}>
            <span className="d-none d-md-inline">{hotIssue.title}</span>
            <span className="d-md-none" style={{ fontSize: '1.5rem' }}>{hotIssue.title}</span>
          </h2>
          <button
            className="btn btn-light border-0 shadow-sm px-3 py-2 d-flex align-items-center gap-2"
            style={{ borderRadius: '999px', fontWeight: 600, fontSize: '1.05rem', color: '#ef4444', background: '#f3f4f6' }}
            onClick={() => {
              const shareUrl = `${window.location.origin}/hot-issue/${hotIssue.id}`;
              navigator.clipboard.writeText(shareUrl);
              toast('핫이슈 상세페이지 링크가 복사되었습니다!');
            }}
          >
            <i className="fas fa-share-alt"></i> 공유하기
          </button>
        </div>
      </Modal.Header>
      {showToast && (
        <div style={{ position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
          <div className="alert alert-success shadow-sm mb-0" style={{ borderRadius: '999px', padding: '0.7em 2em', fontWeight: 600, fontSize: '1.1rem' }}>
            링크가 복사되었습니다!
          </div>
        </div>
      )}
      <Modal.Body className="pt-0" style={{ padding: '2.2rem', background: '#fff', borderRadius: '0 0 1.5rem 1.5rem' }}>
        <div className="mb-4 p-4 rounded-4 shadow-sm d-flex align-items-center gap-3" style={{background: 'linear-gradient(90deg, #f8fafc 60%, #fde68a 100%)'}}>
          <i className="fas fa-fire fa-2x text-danger d-none d-md-block"></i>
          <i className="fas fa-fire text-danger d-md-none" style={{ fontSize: '1.2rem' }}></i>
          <span className="text-dark opacity-90 fw-semibold" style={{ lineHeight: 1.7 }}>
            <span className="d-none d-md-inline fs-5">{hotIssue.description}</span>
            <span className="d-md-none" style={{ fontSize: '0.95rem' }}>{hotIssue.description}</span>
          </span>
        </div>
        <div className="d-flex justify-content-end align-items-center mb-1" style={{ minHeight: '1.5rem' }}>
          <span className="text-muted small" style={{ fontSize: '0.98rem', letterSpacing: '-0.01em' }}>
            {hotIssue.created_at ? new Date(hotIssue.created_at).toLocaleDateString('ko-KR') : ''}
          </span>
        </div>
        {/* 순위 데이터 테이블 */}
        <div className="table-responsive mb-4">
          {loading ? (
            <div className="text-center text-muted py-3">불러오는 중...</div>
          ) : items.length > 0 ? (
            <Table
              className="mb-0"
              hover
              style={
                (document.documentElement.getAttribute('data-theme') === 'dark')
                  ? { borderRadius: '1rem', overflow: 'hidden', background: 'var(--table-bg)', color: 'var(--text-primary)', borderColor: 'var(--table-border)' }
                  : { borderRadius: '1rem', overflow: 'hidden', background: '#f9fafb', color: '#212529', borderColor: '#dee2e6' }
              }
            >
              <thead
                style={
                  (document.documentElement.getAttribute('data-theme') === 'dark')
                    ? { background: 'var(--bg-secondary)', color: 'var(--text-primary)' }
                    : { background: 'linear-gradient(90deg, #ef4444 0%, #fde68a 100%)', color: 'white', fontSize: '1.08rem' }
                }
              >
                <tr>
                  <th style={{ width: '10%' }}>순위</th>
                  <th style={{ width: '15%' }}>국기</th>
                  <th style={{ width: '25%' }}>이름</th>
                  <th style={{ width: '20%' }}>수치</th>
                  <th style={{ width: '30%' }}>설명</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} style={{ verticalAlign: 'middle' }}>
                    <td className="text-center fw-bold">
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.38em 0.7em',
                          fontSize: '1rem',
                          fontWeight: 700,
                          lineHeight: 1,
                          color: 'white',
                          background: (document.documentElement.getAttribute('data-theme') === 'dark')
                            ? 'linear-gradient(90deg, #6366f1 0%, #4f8cff 100%)'
                            : 'linear-gradient(90deg, #ef4444 0%, #fde68a 100%)',
                          borderRadius: '999px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                          marginRight: '0.5em',
                          marginBottom: '0.25em',
                          letterSpacing: '-0.01em'
                        }}
                      >
                        {item.rank}
                      </span>
                    </td>
                    <td className="text-center fs-4">
                      {item.flag ? item.flag : ''}
                    </td>
                    <td className="fw-bold">
                      {item.name}
                    </td>
                    <td>
                      <span className="fw-bold text-danger">
                        {item.value}
                      </span>
                      {item.unit && (
                        <small className="text-muted ms-1">
                          {item.unit}
                        </small>
                      )}
                    </td>
                    <td className="text-muted small">
                      {item.details}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center text-muted py-3">순위 데이터가 없습니다.</div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default HotIssueModal; 